import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Collaborator } from '@/components/services/CollaboratorPicker';

export function useServiceCollaborators(serviceId?: string) {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!serviceId) {
      setCollaborators([]);
      return;
    }

    const fetchCollaborators = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('service_collaborators')
          .select(`
            id,
            user_id,
            percentage,
            profiles:user_id (
              id,
              first_name,
              last_name,
              avatar_url
            )
          `)
          .eq('service_id', serviceId);

        if (error) throw error;

        const mapped: Collaborator[] = (data || []).map((c: any) => ({
          id: c.id,
          user_id: c.user_id,
          percentage: Number(c.percentage),
          profile: c.profiles ? {
            id: c.profiles.id,
            first_name: c.profiles.first_name,
            last_name: c.profiles.last_name,
            avatar_url: c.profiles.avatar_url,
          } : undefined,
        }));

        setCollaborators(mapped);
      } catch (error) {
        console.error('Error fetching collaborators:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCollaborators();
  }, [serviceId]);

  const saveCollaborators = async (serviceId: string, newCollaborators: Collaborator[]) => {
    // Delete existing collaborators
    const { error: deleteError } = await supabase
      .from('service_collaborators')
      .delete()
      .eq('service_id', serviceId);

    if (deleteError) {
      console.error('Error deleting existing collaborators:', deleteError);
      throw deleteError;
    }

    // Insert new collaborators if any
    if (newCollaborators.length > 0) {
      const inserts = newCollaborators.map(c => ({
        service_id: serviceId,
        user_id: c.user_id,
        percentage: c.percentage,
      }));

      const { error: insertError } = await supabase
        .from('service_collaborators')
        .insert(inserts);

      if (insertError) {
        console.error('Error inserting collaborators:', insertError);
        throw insertError;
      }
    }
  };

  return {
    collaborators,
    setCollaborators,
    isLoading,
    saveCollaborators,
  };
}
